import PropTypes from "prop-types";
import { useState, useRef, useEffect } from "react";
import { Box, Button, Typography, Slide, Stack } from "@mui/material";
import { mediaComponent } from "./tutorial-components";
import { CheckBoxIcon, EmptyBoxIcon } from "./tutorial-icons";

// Global constants
const MEDIA_HEIGHT = 340;
const CHECKLIST_HEIGHT = 180;

// The checklist component
export const TutorialChecklist = ({
  list,
  initialChecked,
  onAllCheckUpdate,
  listInstruction = "Mark each step below as complete to proceed.",
  onItemTap,
}) => {
  // The 'checked' list
  const [checkedList, setCheckedList] = useState(
    Array(list.length).fill(initialChecked),
  );
  const firstUnchecked = checkedList.findIndex((item) => !item);
  const allChecked = firstUnchecked < 0;
  const setChecked = (index, checked) => {
    setCheckedList((previous) =>
      previous.map((value, listIndex) => {
        return listIndex === index ? checked : value;
      }),
    );
  };
  const handleTap = async (index) => {
    if (!onItemTap) {
      // No need to wait for third-party actions to be complete;
      // just mark the item right away.
      setChecked(index, !checkedList[index]);
      return;
    }

    // Need to wait until third-party actions are complete with results.
    setChecked(index, await onItemTap(list[index], checkedList[index]));
  };

  // Highlighted item
  const activeIndex = allChecked ? list.length - 1 : firstUnchecked;
  const activeItem = list[activeIndex];

  // Scroll tracking
  const scrollBoxRef = useRef(null);
  const activeRowRef = useRef(null);
  useEffect(() => {
    // Scroll only the checklist box (not ancestors), keeping the active row
    // in view.
    const box = scrollBoxRef.current;
    const row = activeRowRef.current;
    if (!box || !row) return;
    const boxRect = box.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    let delta = 0;
    if (rowRect.top < boxRect.top) {
      delta = rowRect.top - boxRect.top;
    } else if (rowRect.bottom > boxRect.bottom) {
      delta = rowRect.bottom - boxRect.bottom;
    }
    if (delta !== 0) {
      box.scrollBy({ top: delta, behavior: "smooth" });
    }
  }, [activeIndex]);

  // Check handler
  useEffect(() => {
    onAllCheckUpdate(allChecked);
  }, [allChecked, onAllCheckUpdate]);

  // The component
  return (
    <Stack
      spacing={4}
      sx={{
        flex: 1,
        minHeight: 0,
        justifyContent: "center",
        px: 2,
        pt: "8px",
      }}
    >
      {/* Instruction text and media */}
      <CheckListMedia item={activeItem} />

      {/* List of check items */}
      <Stack spacing={1.5} sx={{ flexShrink: 0 }}>
        {/* Checklist instruction */}
        {listInstruction && (
          <Typography
            variant="body2"
            align="center"
            sx={{ color: "text.secondary" }}
          >
            {listInstruction}
          </Typography>
        )}

        {/* The scrollable checklist */}
        <Box
          ref={scrollBoxRef}
          sx={{
            maxHeight: CHECKLIST_HEIGHT,
            flexShrink: 0,
            overflowY: "auto",
            overscrollBehaviorY: "contain",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Stack spacing={1.5}>
            {list.map((item, index) => (
              <CheckListRow
                key={item.id}
                item={item}
                checked={checkedList[index]}
                disabled={index > activeIndex}
                rowRef={index === activeIndex ? activeRowRef : null}
                onTap={() => handleTap(index)}
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};

// Helper components
const CheckListMedia = ({ item }) => {
  return (
    <Box
      sx={{
        // Takes whatever the checklist and the actions leave behind, so the
        // media never runs underneath them.
        flex: 1,
        minHeight: 140,
        width: "100%",
        display: "flex",
        overflow: "hidden",
      }}
    >
      <Slide key={item.id} direction="left" in appear={false}>
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ flex: 1, minHeight: 0 }}
        >
          <Typography
            variant="body1"
            align="center"
            sx={{ color: "text.primary", flexShrink: 0, px: 4 }}
          >
            {item.text}
          </Typography>
          <Box
            sx={{
              width: "100%",
              // grows into the free space, but never taller than the reserve
              flex: 1,
              minHeight: 0,
              maxHeight: MEDIA_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {mediaComponent(item)}
          </Box>
        </Stack>
      </Slide>
    </Box>
  );
};
const CheckListRow = ({ item, checked, disabled, rowRef, onTap }) => {
  return (
    <Button
      ref={rowRef}
      key={item.id}
      fullWidth
      disabled={disabled}
      onClick={onTap}
      endIcon={checked ? <CheckBoxIcon /> : <EmptyBoxIcon color="#a5a5a5" />}
      sx={{
        justifyContent: "space-between",
        textTransform: "none",
        borderRadius: 4,
        px: 2,
        py: 1.5,
        border: 1,
        borderColor: checked ? "primary.main" : "divider",
        color: checked ? "primary.main" : "text.primary",
        "&.Mui-disabled": {
          color: "text.disabled",
          borderColor: "action.disabledBackground",
        },
      }}
    >
      {item.checkLabel}
    </Button>
  );
};

// Prop types
TutorialChecklist.propTypes = {
  list: PropTypes.array.isRequired,
  initialChecked: PropTypes.bool.isRequired,
  onAllCheckUpdate: PropTypes.func.isRequired,
  listInstruction: PropTypes.string,
  onItemTap: PropTypes.func,
};
CheckListMedia.propTypes = {
  item: PropTypes.object.isRequired,
};
CheckListRow.propTypes = {
  item: PropTypes.object.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  rowRef: PropTypes.object,
  onTap: PropTypes.func.isRequired,
};
